import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import AuthLayout from './AuthLayout';
import * as FiIcons from 'react-icons/fi';
import Input from '../ui/Input';
import Button from '../ui/Button';

const { FiMail, FiLock, FiUser, FiBriefcase } = FiIcons;

const AcceptInvite = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const token = searchParams.get('token');
    const { acceptInvite } = useAuth();

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        password: '',
    });

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        localStorage.clear();
        if (!token) {
            toast.error('Invalid or missing invite token');
            navigate('/login');
        }
    }, [token, navigate]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const getPasswordStrength = (pass) => {
        if (!pass) return 0;
        let strength = 0;
        if (pass.length >= 8) strength += 25;
        if (/[A-Z]/.test(pass)) strength += 25;
        if (/[0-9]/.test(pass)) strength += 25;
        if (/[^A-Za-z0-9]/.test(pass)) strength += 25;
        return strength;
    };

    const strength = getPasswordStrength(formData.password);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.firstName || !formData.lastName || !formData.password) {
            toast.error('All fields are required');
            return;
        }

        try {
            setLoading(true);
            acceptInvite(token, formData.password, formData.firstName, formData.lastName);
            toast.success('Account created successfully!');
            navigate('/login');

        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout
            title={"Accept Invite"}
            subtitle={"Start your 14-day free trial today."}
        >
            <form onSubmit={handleSubmit} className="space-y-5">

                <div className="grid grid-cols-2 gap-4">
                    <Input
                        label="First Name"
                        placeholder="John"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        icon={FiUser}
                        required
                    />
                    <Input
                        label="Last Name"
                        placeholder="Doe"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                    />
                </div>

                <div className="space-y-2">
                    <Input
                        label="Password"
                        placeholder="••••••••"
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        icon={FiLock}
                        required
                    />
                    <div className="space-y-1.5">
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-500 dark:text-gray-400">Password strength</span>
                            <span className={strength >= 75 ? "text-green-500 font-medium" : strength >= 50 ? "text-yellow-500 font-medium" : "text-red-500 font-medium"}>
                                {strength >= 75 ? 'Strong' : strength >= 50 ? 'Fair' : 'Weak'}
                            </span>
                        </div>
                        <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                            <div
                                className={`h-full transition-all duration-500 ${strength >= 75 ? 'bg-green-500' : strength >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                style={{ width: `${strength}%` }}
                            />
                        </div>
                    </div>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400">
                    By signing up, you agree to our{' '}
                    <Link to="/terms" className="text-indigo-600 hover:underline dark:text-indigo-400">Terms of Service</Link> and{' '}
                    <Link to="/privacy" className="text-indigo-600 hover:underline dark:text-indigo-400">Privacy Policy</Link>.
                </p>

                <Button type="submit" className="w-full" isLoading={loading}>
                    {'Accept Invite'}
                </Button>

                <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-8">
                    Already have an account?{' '}
                    <Link to="/login" className="font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400">
                        Sign in
                    </Link>
                </p>
            </form>
        </AuthLayout>

        // <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        //     <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        //         <h2 className="text-2xl font-bold mb-6 text-center text-gray-800 dark:text-white">
        //             Accept Invitation
        //         </h2>

        //         <form onSubmit={handleSubmit} className="space-y-4">

        //             <input
        //                 type="text"
        //                 name="firstName"
        //                 placeholder="First Name"
        //                 value={formData.firstName}
        //                 onChange={handleChange}
        //                 autoComplete="false"
        //                 autoFocus
        //                 className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
        //             />

        //             <input
        //                 type="text"
        //                 name="lastName"
        //                 placeholder="Last Name"
        //                 value={formData.lastName}
        //                 onChange={handleChange}
        //                 autoComplete='false'
        //                 className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
        //             />

        //             <input
        //                 type="password"
        //                 name="password"
        //                 placeholder="Password"
        //                 value={formData.password}
        //                 onChange={handleChange}
        //                 className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
        //             />

        //             <button
        //                 type="submit"
        //                 disabled={loading}
        //                 className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition"
        //             >
        //                 {loading ? 'Creating Account...' : 'Accept Invite'}
        //             </button>
        //         </form>
        //     </div>
        // </div>
    );
};

export default AcceptInvite;